import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { Controller, useForm, type Control } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { api } from "../../../../services/api";
import { CadastroService } from "../../../../services/auth/cadastro";

// Definição dos tipos para cada schema
type CNPJFormData = z.infer<typeof cnpjSchema>;
type ONGInfoFormData = z.infer<typeof ongInfoSchema>;
type AddressFormData = z.infer<typeof addressSchema>;
type ResponsavelFormData = z.infer<typeof responsavelSchema>;

// Define validation schemas for each step
const cnpjSchema = z.object({
  cnpj: z
    .string()
    .min(14, "CNPJ precisa ter 14 dígitos")
    .max(18, "CNPJ inválido")
    .refine((val) => validateCNPJ(val), "CNPJ inválido"),
});

const ongInfoSchema = z.object({
  nome: z.string().min(3, "Nome precisa ter no mínimo 3 caracteres"),
  razaoSocial: z.string().min(3, "Razão social precisa ter no mínimo 3 caracteres"),
  email: z.string().email("E-mail inválido"),
  telefone: z.string().min(10, "Telefone inválido").max(15, "Telefone inválido"),
  senha: z.string().min(6, "Senha precisa ter no mínimo 6 caracteres"),
});

const addressSchema = z.object({
  cep: z.string().min(8, "CEP inválido").max(9, "CEP inválido"),
  logradouro: z.string().min(3, "Logradouro inválido"),
  numero: z.string().optional(),
  complemento: z.string().optional(),
  bairro: z.string().min(2, "Bairro inválido"),
  cidade: z.string().min(2, "Cidade inválida"),
  estado: z
    .string()
    .min(2, "Estado inválido")
    .max(2, "Use a sigla do estado (ex: SP)"),
});

const responsavelSchema = z.object({
  responsavel: z.string().min(3, "Nome precisa ter no mínimo 3 caracteres"),
});

// Interface para a resposta da API de CEP
interface ViaCEPResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

// Interface para a resposta da API de CNPJ
export interface CNPJResponse {
  uf: string;
  cep: string;
  qsa: Qsa[];
  cnpj: string;
  pais: unknown;
  email: unknown;
  porte: string;
  bairro: string;
  numero: string;
  ddd_fax: string;
  municipio: string;
  logradouro: string;
  cnae_fiscal: number;
  codigo_pais: unknown;
  complemento: string;
  codigo_porte: number;
  razao_social: string;
  nome_fantasia: string;
  capital_social: number;
  ddd_telefone_1: string;
  ddd_telefone_2: string;
  opcao_pelo_mei: boolean;
  descricao_porte: string;
  codigo_municipio: number;
  cnaes_secundarios: CnaesSecundario[];
  natureza_juridica: string;
  regime_tributario: unknown;
  situacao_especial: string;
  opcao_pelo_simples: boolean;
  situacao_cadastral: number;
  data_opcao_pelo_mei: string;
  data_exclusao_do_mei: string;
  cnae_fiscal_descricao: string;
  codigo_municipio_ibge: number;
  data_inicio_atividade: string;
  data_situacao_especial: unknown;
  data_opcao_pelo_simples: string;
  data_situacao_cadastral: string;
  nome_cidade_no_exterior: string;
  codigo_natureza_juridica: number;
  data_exclusao_do_simples: unknown;
  motivo_situacao_cadastral: number;
  ente_federativo_responsavel: string;
  identificador_matriz_filial: number;
  qualificacao_do_responsavel: number;
  descricao_situacao_cadastral: string;
  descricao_tipo_de_logradouro: string;
  descricao_motivo_situacao_cadastral: string;
  descricao_identificador_matriz_filial: string;
}

export interface Qsa {
  pais: unknown;
  nome_socio: string;
  codigo_pais: unknown;
  faixa_etaria: string;
  cnpj_cpf_do_socio: string;
  qualificacao_socio: string;
  codigo_faixa_etaria: number;
  data_entrada_sociedade: string;
  identificador_de_socio: number;
  cpf_representante_legal: string;
  nome_representante_legal: string;
  codigo_qualificacao_socio: number;
  qualificacao_representante_legal: string;
  codigo_qualificacao_representante_legal: number;
}

export interface CnaesSecundario {
  codigo: number;
  descricao: string;
}

// Interface para a resposta da API de validação de email e telefone
// true = já existe no banco, false = não existe (pode usar)
interface ValidateEmailTelefoneResponse {
  email: boolean;
  telefone: boolean;
}

// CNPJ validation helper function
function validateCNPJ(cnpj: string): boolean {
  // Remove non-digits
  cnpj = cnpj.replace(/[^\d]/g, "");

  // Check if length is 14
  if (cnpj.length !== 14) return false;

  // Check if all digits are the same
  if (/^(\d)\1+$/.test(cnpj)) return false;

  // Validate first check digit
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }

  let remainder = sum % 11;
  const checkDigit1 = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cnpj.charAt(12)) !== checkDigit1) return false;

  // Validate second check digit
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }

  remainder = sum % 11;
  const checkDigit2 = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(cnpj.charAt(13)) === checkDigit2;
}

// Tipo para as props do componente FormInput
interface FormInputProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  placeholder: string;
  type?: string;
  onBlur?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface CadastroONGProps {
  onBack: () => void;
}

function CadastroONG({ onBack }: CadastroONGProps) {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [termoAceito, setTermoAceito] = useState<boolean>(false);

  const navigate = useNavigate();
  const cadastroService = new CadastroService();

  // Form for step 1 (CNPJ validation)
  const cnpjForm = useForm<CNPJFormData>({
    resolver: zodResolver(cnpjSchema),
    defaultValues: {
      cnpj: "",
    },
  });

  // Form for step 2 (ONG info)
  const ongInfoForm = useForm<ONGInfoFormData>({
    resolver: zodResolver(ongInfoSchema),
    defaultValues: {
      nome: "",
      razaoSocial: "",
      email: "",
      telefone: "",
      senha: "",
    },
  });

  // Form for step 3 (Address)
  const addressForm = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
    },
  });

  // Form for step 4 (Responsável)
  const responsavelForm = useForm<ResponsavelFormData>({
    resolver: zodResolver(responsavelSchema),
    defaultValues: {
      responsavel: "",
    },
  });

  // React Query Mutations
  const validateCNPJMutation = useMutation({
    mutationFn: (cnpj: string) => cadastroService.validateCNPJ(cnpj),
    onSuccess: async (data) => {
      console.log(data);
      
      if (data) {
        alert("CNPJ já cadastrado. Por favor, entre em contato com o suporte.");
      } else {
        fetchCNPJData(cnpjForm.getValues("cnpj"));
      }
    },
    onError: (error) => {
      console.error("Erro ao validar CNPJ:", error);
      alert("Erro ao validar CNPJ. Por favor, tente novamente.");
    },
  });

  // Validação de email e telefone
  const validateEmailTelefoneMutation = useMutation<
    ValidateEmailTelefoneResponse,
    Error,
    { email: string; telefone: string }
  >({
    mutationFn: async (data: { email: string; telefone: string }): Promise<ValidateEmailTelefoneResponse> => {
      const response = await cadastroService.ONGvalidateEmailTelefone(data.email, data.telefone);
      return response.data;
    },
    onSuccess: (data) => {
      // Se retornar true, significa que já existe no banco
      if (!data.email && !data.telefone) {
        // Ambos são false (não existem), pode prosseguir
        setCurrentStep(3);
      } else {
        if (data.email) ongInfoForm.setError("email", { message: "E-mail já está em uso" });
        if (data.telefone) ongInfoForm.setError("telefone", { message: "Telefone já está em uso" });
      }
    },
    onError: (error) => {
      console.error("Erro ao validar email e telefone:", error);
      alert("Erro ao validar email e telefone. Por favor, tente novamente.");
    },
  });

  // Mutation for fetching CNPJ data
  const cnpjDataMutation = useMutation({
    mutationFn: async (cnpj: string) => {
      const cleanCnpj = cnpj.replace(/[^\d]/g, "");
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`);
      
      return await response.json() as CNPJResponse;
    },
    onSuccess: (data) => {
      if (data.cnpj) {
        // Preencher formulário da ONG
        ongInfoForm.setValue("nome", data.nome_fantasia || "");
        ongInfoForm.setValue("razaoSocial", data.razao_social || "");
        ongInfoForm.setValue("email", (data.email && typeof data.email === 'string') ? data.email : "");
        ongInfoForm.setValue("telefone", data.ddd_telefone_1 || "");

        // Preencher formulário de endereço
        if (data.cep) {
          addressForm.setValue("cep", data.cep);
          addressForm.setValue("logradouro", data.logradouro || "");
          addressForm.setValue("numero", data.numero || "");
          addressForm.setValue("complemento", data.complemento || "");
          addressForm.setValue("bairro", data.bairro || "");
          addressForm.setValue("cidade", data.municipio || "");
          addressForm.setValue("estado", data.uf || "");
        }

        setCurrentStep(2);
      } else {
        alert("CNPJ não encontrado, os dados deverão ser preenchidos manualmente");
        setCurrentStep(2);
      }
    },
    onError: () => {
      alert("Erro ao buscar dados do CNPJ, os dados deverão ser preenchidos manualmente");
      setCurrentStep(2);
    },
  });

  type ONGFormData = {
    nome: string;
    razaoSocial: string;
    responsavel: string;
    cnpj: string;
    email: string;
    password: string;
    telefone: string;
    logradouro: string;
    numero: string | "";
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
  };

  const createONGMutation = useMutation({
    mutationFn: (formData: ONGFormData) => {
      return cadastroService.createONG({
        ...formData,
        cnpj: formData.cnpj.replace(/[^\d]/g, ""),
        telefone: formData.telefone.replace(/[^\d]/g, "")
      });
    },
    onSuccess: () => {
      alert("Sucesso: Cadastro da ONG realizado com sucesso!");
      navigate("/");
    },
    onError: (error) => {
      console.error("Erro ao cadastrar ONG:", error);
      alert("Erro ao cadastrar ONG. Por favor, tente novamente.");
    },
  });

  // CEP Query using React Query
  const fetchCEP = async (cep: string) => {
    if (!cep || cep.replace(/[^0-9]/g, "").length !== 8) {
      return null;
    }

    const cleanCep = cep.replace(/[^0-9]/g, "");
    const response = await api.get(`/buscar-cep/${cleanCep}?cidadeObrigatoria=false`);
    const data = response.data as ViaCEPResponse;
    
    return data;
  };

  const cepMutation = useMutation({
    mutationFn: fetchCEP,
    onSuccess: (data) => {
      if (data && !data.erro) {
        addressForm.setValue("logradouro", data.logradouro);
        addressForm.setValue("bairro", data.bairro);
        addressForm.setValue("cidade", data.localidade);
        addressForm.setValue("estado", data.uf);
      } else if (data?.erro) {
        alert("CEP não encontrado");
      }
    },
    onError: (error: unknown) => {
      // Verificar se é erro de conexão/rede (já tratado pelo interceptor)
      if (error && typeof error === 'object') {
        const axiosError = error as {
          code?: string;
          message?: string;
          response?: { data?: { error?: string } };
        };

        // Detectar erros de conexão (connection refused, network error, etc)
        const isConnectionError =
          !axiosError.response ||
          axiosError.code === "ECONNREFUSED" ||
          axiosError.code === "ERR_NETWORK" ||
          axiosError.code === "NETWORK_ERROR" ||
          axiosError.message?.toLowerCase().includes("network error") ||
          axiosError.message?.toLowerCase().includes("connection refused") ||
          axiosError.message?.toLowerCase().includes("failed to fetch");

        // Se for erro de conexão, não mostrar alert (já foi mostrado toast pelo interceptor)
        if (isConnectionError) {
          return;
        }

        // Para outros erros, mostrar mensagem específica
        const errorMessage =
          axiosError.response?.data?.error || "Erro ao buscar CEP";
        alert(errorMessage);
      } else {
        alert("Erro ao buscar CEP");
      }
    },
  });

  const handleValidateCNPJ = async (): Promise<void> => {
    const isValid = await cnpjForm.trigger("cnpj");

    if (!isValid) {
      alert("CNPJ inválido");
      return;
    }

    validateCNPJMutation.mutate(cnpjForm.getValues("cnpj"));
  };

  const fetchCNPJData = (cnpj: string): void => {
    cnpjDataMutation.mutate(cnpj);
  };

  // Handle ONG info submission
  const handleONGInfoSubmit = async (data: ONGInfoFormData): Promise<void> => {
    // Validar email e telefone antes de prosseguir
    validateEmailTelefoneMutation.mutate({
      email: data.email,
      telefone: data.telefone
    });
  };

  // Handle address submission
  const handleAddressSubmit = async (): Promise<void> => {
    setCurrentStep(4);
  };

  // Handle responsável submission and advance to terms
  const handleResponsavelSubmit = async (): Promise<void> => {
    setCurrentStep(5);
  };

  // Handle final registration after accepting terms
  const handleFinalSubmit = async (): Promise<void> => {
    if (!termoAceito) {
      alert("Erro: Você precisa aceitar os termos para continuar.");
      return;
    }

    const formData: ONGFormData = {
      cnpj: cnpjForm.getValues("cnpj"),
      nome: ongInfoForm.getValues("nome"),
      razaoSocial: ongInfoForm.getValues("razaoSocial"),
      email: ongInfoForm.getValues("email"),
      telefone: ongInfoForm.getValues("telefone"),
      password: ongInfoForm.getValues("senha"),
      logradouro: addressForm.getValues("logradouro"),
      numero: addressForm.getValues("numero") || "",
      bairro: addressForm.getValues("bairro"),
      cidade: addressForm.getValues("cidade"),
      estado: addressForm.getValues("estado"),
      cep: addressForm.getValues("cep"),
      responsavel: responsavelForm.getValues("responsavel")
    };
    
    createONGMutation.mutate(formData);
  };

  // Handle CEP search
  const handleCEPSearch = async (): Promise<void> => {
    const cep = addressForm.getValues("cep").replace(/[^0-9]/g, "");
    if (cep.length !== 8) return;

    cepMutation.mutate(cep);
  };

  // Custom input component
  const FormInput: React.FC<FormInputProps> = ({
    control,
    name,
    placeholder,
    type = "text",
    onBlur = undefined,
    disabled = false,
    icon,
  }) => (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur: fieldOnBlur, value },
        fieldState: { error },
      }) => (
        <div className="mb-3 sm:mb-4">
          <div
            className={`flex items-center w-full bg-white rounded-full h-14 sm:h-16 px-3 sm:px-4 border ${
              error ? "border-red-500" : "border-gray-300"
            } ${disabled ? "bg-gray-100" : ""}`}
          >
            {icon && <div className="mr-2 sm:mr-3">{icon}</div>}
            <input
              disabled={disabled}
              className="flex-1 text-gray-800 text-sm sm:text-base outline-none"
              placeholder={placeholder}
              onChange={onChange}
              onBlur={() => {
                fieldOnBlur();
                if (onBlur) onBlur();
              }}
              value={value ?? ""}
              type={type}
              autoComplete="off"
              autoCorrect="off"
            />
          </div>
          {error && (
            <p className="text-red-500 text-xs ml-3 sm:ml-4 mt-1">
              {error.message}
            </p>
          )}
        </div>
      )}
    />
  );

  // Button with loading state
  const ActionButton: React.FC<{
    title: string;
    onPress: () => void;
    isLoading?: boolean;
    backgroundColor?: string;
    textColor?: string;
    width?: string;
  }> = ({
    title,
    onPress,
    isLoading = false,
    backgroundColor = "#10b981",
    textColor = "white",
    width = "full",
  }) => (
    <button
      className={`${width.includes('w-') ? width : `w-${width}`} rounded-full h-14 sm:h-16 flex items-center justify-center font-semibold text-base sm:text-lg ${
        isLoading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
      }`}
      style={{ backgroundColor, color: textColor }}
      onClick={onPress}
      disabled={isLoading}
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
      ) : (
        title
      )}
    </button>
  );

  // Progress bar
  const ProgressBar: React.FC = () => (
    <div className="mb-6 mt-2 px-2">
      <div className="flex justify-between mb-2">
        <span className="text-xs sm:text-sm text-gray-500 font-medium">
          Passo {currentStep} de 5
        </span>
        <span className="text-xs sm:text-sm text-green-500 font-medium">
          {Math.round((currentStep / 5) * 100)}%
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-green-500 rounded-full transition-all duration-300"
          style={{ width: `${(currentStep / 5) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <ProgressBar />
      <div className="bg-white px-3 sm:px-6 py-4 rounded-2xl sm:rounded-3xl mx-2 sm:mx-0 w-full max-w-full">
        <h2 className="text-lg sm:text-xl font-semibold mb-2 text-gray-800">
          Cadastro de ONG
        </h2>

        {currentStep === 1 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Vamos começar com a validação do CNPJ da sua ONG
            </p>

            <FormInput
              control={cnpjForm.control}
              name="cnpj"
              placeholder="CNPJ (apenas números)"
              type="text"
            />

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={onBack}
              >
                Voltar
              </button>

              <ActionButton
                title="Validar CNPJ"
                onPress={handleValidateCNPJ}
                isLoading={validateCNPJMutation.isPending || cnpjDataMutation.isPending}
                width="full sm:w-1/2 order-1 sm:order-2"
              />
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Dados da organização
            </p>

            <FormInput
              control={ongInfoForm.control}
              name="nome"
              placeholder="Nome fantasia"
              disabled={cnpjDataMutation.isSuccess}
            />

            <FormInput
              control={ongInfoForm.control}
              name="razaoSocial"
              placeholder="Razão social"
              disabled={cnpjDataMutation.isSuccess}
            />

            <FormInput
              control={ongInfoForm.control}
              name="email"
              placeholder="E-mail"
              type="email"
            />

            <FormInput
              control={ongInfoForm.control}
              name="telefone"
              placeholder="Telefone"
              type="tel"
            />

            <FormInput
              control={ongInfoForm.control}
              name="senha"
              placeholder="Senha"
              type="password"
            />

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={() => setCurrentStep(1)}
              >
                Voltar
              </button>

              <ActionButton
                title="Próximo"
                onPress={ongInfoForm.handleSubmit(handleONGInfoSubmit)}
                isLoading={validateEmailTelefoneMutation.isPending}
                width="full sm:w-1/2 order-1 sm:order-2"
              />
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Endereço da ONG
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2 mb-4">
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="cep"
                  placeholder="CEP"
                  type="text"
                  onBlur={handleCEPSearch}
                  disabled={cnpjDataMutation.isSuccess}
                />
              </div>
              <button
                className={`w-full sm:w-auto bg-gray-200 rounded-full h-14 sm:h-16 px-4 sm:px-6 flex items-center justify-center font-semibold hover:bg-gray-300 transition-colors text-sm sm:text-base ${
                  cepMutation.isPending || cnpjDataMutation.isSuccess ? "opacity-70 cursor-not-allowed" : ""
                }`}
                onClick={handleCEPSearch}
                disabled={cepMutation.isPending || cnpjDataMutation.isSuccess}
              >
                {cepMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                ) : (
                  <span className="text-gray-700">Buscar CEP</span>
                )}
              </button>
            </div>

            <FormInput
              control={addressForm.control}
              name="logradouro"
              placeholder="Logradouro"
              disabled={cnpjDataMutation.isSuccess}
            />

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="numero"
                  placeholder="Número"
                  type="text"
                  disabled={cnpjDataMutation.isSuccess}
                />
              </div>
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="complemento"
                  placeholder="Complemento"
                />
              </div>
            </div>

            <FormInput
              control={addressForm.control}
              name="bairro"
              placeholder="Bairro"
              disabled={cnpjDataMutation.isSuccess}
            />

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="cidade"
                  placeholder="Cidade"
                  disabled={cnpjDataMutation.isSuccess}
                />
              </div>
              <div className="w-full sm:w-24">
                <FormInput
                  control={addressForm.control}
                  name="estado"
                  placeholder="UF"
                  disabled={cnpjDataMutation.isSuccess}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={() => setCurrentStep(2)}
              >
                Voltar
              </button>

              <ActionButton
                title="Próximo"
                onPress={addressForm.handleSubmit(handleAddressSubmit)}
                width="full sm:w-1/2 order-1 sm:order-2"
              />
            </div>
          </>
        )}

        {currentStep === 4 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Dados do responsável legal
            </p>

            <FormInput
              control={responsavelForm.control}
              name="responsavel"
              placeholder="Nome completo"
            />

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={() => setCurrentStep(3)}
              >
                Voltar
              </button>

              <ActionButton
                title="Próximo"
                onPress={responsavelForm.handleSubmit(handleResponsavelSubmit)}
                width="full sm:w-1/2 order-1 sm:order-2"
              />
            </div>
          </>
        )}

        {currentStep === 5 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Termos de Uso e Políticas de Privacidade
            </p>

            <div 
              className="bg-gray-50 rounded-lg mb-4 sm:mb-6 p-3 sm:p-4 overflow-y-auto"
              style={{ maxHeight: '250px' }}
            >
              <div className="text-gray-700 text-xs sm:text-sm leading-5 sm:leading-6">
                <p className="font-bold mb-4">TERMOS DE USO</p>
                
                <p className="mb-4">
                  Ao utilizar nosso aplicativo de agendamento veterinário, você concorda com os seguintes termos:
                </p>
                
                <p className="font-semibold mb-2">1. Uso do Aplicativo</p>
                <p className="mb-4">
                  O aplicativo destina-se exclusivamente ao agendamento de consultas veterinárias. Você se compromete a fornecer informações verdadeiras e atualizadas.
                </p>
                
                <p className="font-semibold mb-2">2. Responsabilidades</p>
                <p className="mb-4">
                  Você é responsável por manter suas credenciais de acesso seguras e por todos os agendamentos realizados em sua conta.
                </p>
                
                <p className="font-semibold mb-2">3. Privacidade</p>
                <p className="mb-4">
                  Seus dados pessoais serão tratados conforme nossa Política de Privacidade, respeitando a LGPD.
                </p>
                
                <p>
                  Ao aceitar estes termos, você concorda em receber notificações sobre seus agendamentos.
                </p>
              </div>
            </div>

            <button
              className="flex items-start sm:items-center mb-4 sm:mb-6 cursor-pointer"
              onClick={() => setTermoAceito(!termoAceito)}
            >
              <div
                className={`w-5 h-5 sm:w-6 sm:h-6 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0 ${
                  termoAceito ? "bg-green-500 border-green-500" : "border-gray-300"
                }`}
              >
                {termoAceito && (
                  <span className="text-white font-bold text-xs sm:text-sm">✓</span>
                )}
              </div>
              <span className="text-gray-700 flex-1 text-xs sm:text-sm">
                Eu li e aceito os termos de uso e políticas de privacidade
              </span>
            </button>

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={() => setCurrentStep(4)}
              >
                Voltar
              </button>

              <button
                className={`w-full sm:w-1/2 bg-green-600 text-white rounded-full h-14 sm:h-16 flex items-center justify-center font-semibold text-base sm:text-lg order-1 sm:order-2 ${
                  !termoAceito || createONGMutation.isPending ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700"
                } transition-colors`}
                onClick={handleFinalSubmit}
                disabled={!termoAceito || createONGMutation.isPending}
              >
                {createONGMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                ) : (
                  "Finalizar Cadastro"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CadastroONG;